import * as React from 'react';

import {
    useDataTable, useLoading, useMessage, useRemoteData, useSearch, useTranslation, Tag
} from '@components';
import type { IMachinery, IOption, ISecurity, IWork, ModuleManager } from '@models';
import * as S from '@utils/security';
import './MachineryJobsDialog.scss'

type ActivateF = (workId: number, jobId: number, machineryId: number, jobHasContractorId: number | undefined) => Promise<IOption<number>>;
type GetMachineryF = (workId: number, id: number) => Promise<IMachinery>;
type GetJobF = (workId: number, jobId: number) => Promise<any>;

export interface IProps {
    requestClose: Function;
    activateJobHasMachinery: ActivateF;
    saveJobHasMachinery: Function;
    security: ISecurity;
    machinery: IMachinery;
    machineryId: number;
    machineryTitle?: string;
    moduleManager: ModuleManager;
    getMachinery: GetMachineryF;
    work: IWork;
    removeJobHasMachinery: Function;
    getJob: GetJobF;
}

// type SearchR = {
//     endDate?: Date;
//     jobId: number;
//     jobHasMachineryId?: number;
//     isRelated?: boolean;
//     currentStatusType?: number;
//     parentContractorName?: string;
//     jobHasContractorId?: number;
//     name?: string;
//     code?: string;
//     description?: string;
// }

function MachineryJobsForm(props: IProps) {
    const { t } = useTranslation();
    const loading = useLoading();
    const messages = useMessage();

    const search = useSearch<any>({
        search: 'contractorJobsHasMachinery.list',
        normalizeKeys: true,
        workId: props.work.id,
        filters: {
            machineryId: props.machineryId,
            contractorId: props.machinery.contractorId
        }
    });

    React.useEffect(() => {
        search.doSearch();
    }, []);

    const saveMachineryRelation = loading.wrap(async (jobId: number, jobHasContractorId: number) => {
        const data = {
            machineryId: props.machineryId,
            jobId,
        };
        const resp = await props.saveJobHasMachinery(props.work.id, jobId, data, jobHasContractorId);
        messages.set(resp);
        await search.doSearch();
    });

    const activateMachineryRelation = loading.wrap(async (jobId: number, d: any) => {
        const resp = await props.activateJobHasMachinery(props.work.id, jobId, props.machineryId, d.jobHasMachineryId);
        messages.set(resp);
        await search.doSearch();
    });

    const removeMachineryRelation = loading.wrap(async (jobId: number, jobHasMachineryId: number) => {
        const resp = await props.removeJobHasMachinery(props.work.id, jobId, jobHasMachineryId);
        messages.set(resp);
        await search.doSearch();
    });

    const [jobPerms, setJobPerms] = React.useState<Record<number, { add: boolean; remove: boolean }>>({});

    React.useEffect(() => {
        if (!search.value || !search.value.length) {
            setJobPerms({});
            return;
        }

        const jobIds = Array.from(new Set(search.value.map(r => r.jobId)));
        let cancelled = false;

        const load = async () => {
            const results: Record<number, { add: boolean; remove: boolean }> = {};
            const isGestor = props.security.isGestor();

            const promises = jobIds.map(async (jobId) => {
                try {
                    const resp = await props.getJob(props.work.id, jobId);
                    if (cancelled) return;
                    const job = resp.data?.work?.job;
                    if (!job) return;

                    const responsibleIds = job.departments
                        ?.filter((d: any) => d.isActive !== false)
                        ?.map((d: any) => d.responsibleId) ?? [];
                    const isResponsible = responsibleIds.some((rid: number) => props.security.getWorkerIds().includes(rid));

                    const evalUser = {
                        ...(props.security.user ?? {}),
                        workerIds: props.security.getWorkerIds(),
                        contractorIds: (props.security as any).getContractorIds?.() ?? [],
                    };
                    let add = isGestor || isResponsible || S.hasPermission('jobs.machineries.add', { user: evalUser as any, work: props.work, job });
                    let remove = isGestor || isResponsible || S.hasPermission('jobHasMachinery.remove', { user: evalUser as any, work: props.work, job });

                    if (!cancelled) {
                        results[jobId] = { add, remove };
                    }
                } catch (e) {
                    console.error('Failed to fetch permissions for job', jobId, e);
                }
            });

            await Promise.all(promises);

            if (!cancelled) {
                setJobPerms(results);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [search.value, props.security, props.work.id]);

    const dataTable = useDataTable<any>({
        columns: [
            { title: 'Job', render: (d: any) => <><Tag value={d.code} /><span className='sm pd-left' title={d.name}>{d.name}</span></> },
            {
                title: 'Description',
                render: (d: any) => <span title={d.description}>{d.description}</span>,
                field: 'description',
            },
            {
                title: 'Contractor',
                field: 'parentContractorName',
                render: (d: any) => <span title={d.parentContractorName}>{d.parentContractorName}</span>
            },
            {
                title: 'Associated',
                delegate: 'boolean',
                field: 'isRelated',
                className: 'td-md'
            },
            {
                title: 'End date',
                delegate: 'date',
                field: 'endDate',
                className: 'td-lg',
            },
        ],
        actions: [
            {
                actions: [
                    {
                        title: t('Associate'),
                        onClick: d => saveMachineryRelation(d.jobId, d.jobHasContractorId),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p === undefined) return true;
                            if (!p.add) return true;
                            return d.isRelated === true || d.currentStatusType === 3;
                        },
                    },
                    {
                        title: t('Reactivate'),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p === undefined) return true;
                            if (!p.add) return true;
                            return d.isRelated !== true || d.endDate == null;
                        },
                        onClick: d => activateMachineryRelation(d.jobId, d)
                    },
                    {
                        title: t('Finalize'),
                        disabled: d => {
                            const p = jobPerms[d.jobId];
                            if (p === undefined) return true;
                            if (!p.remove) return true;
                            return !d.isRelated || d.endDate != null;
                        },
                        onClick: d => removeMachineryRelation(d.jobId, d.jobHasMachineryId)
                    },
                ]
            }
        ],
        data: search.value
    })

    return <div>
        {search.renderLoading()}
        {dataTable()}
        <div className='footer r r-end'>
            <button onClick={() => props.requestClose()}>{t('Close')}</button>
        </div>
    </div>
}

export function MachineryJobsDialog(props: IProps) {
    const machinery = useRemoteData<IMachinery>(props.getMachinery, {
        parameters: [props.work.id, props.machineryId]
    });

    return <div className='MachineryJobsDialog'>
        {machinery.renderLoading()}
        {machinery.value && <MachineryJobsForm
            {...props}
            machinery={machinery.value} />}
    </div>
}
